import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./styles/docs.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Analytics } from "@vercel/analytics/react";
import Home from "./pages/Home";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import BlogDocs from "./pages/BlogDocs";
import Contact from "./pages/Contact";
import Download from "./pages/Download";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Docs from "./pages/Docs";
import DocsHome from "./pages/DocsHome";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyEmailRequired from "./pages/VerifyEmailRequired";
import Dashboard from "./pages/Dashboard";
import LicenseDetails from "./pages/LicenseDetails";
import PurchaseHistory from "./pages/PurchaseHistory";
import PurchaseSuccess from "./pages/PurchaseSuccess";
import AdminDashboard from "./pages/AdminDashboard";
import AdminLicenses from "./pages/AdminLicenses";
import AdminCreateLicense from "./pages/AdminCreateLicense";
import AdminUsers from "./pages/AdminUsers";
import AdminEmails from "./pages/AdminEmails";
import AdminEmailRules from "./pages/AdminEmailRules";
import ProtectedRoute from "./components/ProtectedRoute";

// Component to conditionally render Header based on route
function ConditionalHeader() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return null;
  }

  return <Header />;
}

// Component to conditionally render Footer based on route
function ConditionalFooter() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return null;
  }

  return <Footer />;
}

// Lazy load documentation pages
const Introduction = lazy(() => import("./docs/getting-started/introduction.mdx"));
const QuickStart = lazy(() => import("./docs/getting-started/quick-start.mdx"));
const Installation = lazy(() => import("./docs/getting-started/installation.mdx"));
const SystemRequirements = lazy(() => import("./docs/getting-started/system-requirements.mdx"));
const Licensing = lazy(() => import("./docs/getting-started/licensing.mdx"));

// Core Features
const ChatInterface = lazy(() => import("./docs/core-features/chat-interface.mdx"));
const AIProviders = lazy(() => import("./docs/core-features/ai-providers.mdx"));
const MCPTools = lazy(() => import("./docs/core-features/mcp-tools.mdx"));
const BrowserAutomation = lazy(() => import("./docs/core-features/browser-automation.mdx"));
const Notes = lazy(() => import("./docs/core-features/notes.mdx"));

// Advanced Features
const PRReview = lazy(() => import("./docs/advanced-features/pr-review.mdx"));
const AutomatedTesting = lazy(() => import("./docs/advanced-features/automated-testing.mdx"));
const BugReproduction = lazy(() => import("./docs/advanced-features/bug-reproduction.mdx"));
const ScheduledTasks = lazy(() => import("./docs/advanced-features/scheduled-tasks.mdx"));
const Dictation = lazy(() => import("./docs/advanced-features/dictation.mdx"));
const HooksSystem = lazy(() => import("./docs/advanced-features/hooks-system.mdx"));
const WindowsAutomation = lazy(() => import("./docs/advanced-features/windows-automation.mdx"));
const ExploratoryTesting = lazy(() => import("./docs/advanced-features/exploratory-testing.mdx"));
const DependencyMap = lazy(() => import("./docs/advanced-features/dependency-map.mdx"));
const Recall = lazy(() => import("./docs/advanced-features/recall.mdx"));
const RAGLibrary = lazy(() => import("./docs/advanced-features/rag-library.mdx"));
const CodeAgent = lazy(() => import("./docs/advanced-features/code-agent.mdx"));
const DNDAssistant = lazy(() => import("./docs/advanced-features/dnd-assistant.mdx"));

// Configuration
const SettingsOverview = lazy(() => import("./docs/configuration/settings-overview.mdx"));
const AIConfiguration = lazy(() => import("./docs/configuration/ai-configuration.mdx"));
const MCPServers = lazy(() => import("./docs/configuration/mcp-servers.mdx"));
const BrowserProfiles = lazy(() => import("./docs/configuration/browser-profiles.mdx"));
const Themes = lazy(() => import("./docs/configuration/themes.mdx"));

// Guides
const AzureDevOpsSetup = lazy(() => import("./docs/guides/azure-devops-setup.mdx"));
const SQLDatabaseSetup = lazy(() => import("./docs/guides/sql-database-setup.mdx"));
const OutlookIntegration = lazy(() => import("./docs/guides/outlook-integration.mdx"));
const WorkflowExamples = lazy(() => import("./docs/guides/workflow-examples.mdx"));
const BestPractices = lazy(() => import("./docs/guides/best-practices.mdx"));
const Dynamics365PowerPlatform = lazy(() => import("./docs/guides/dynamics-365-power-platform.mdx"));

// Troubleshooting
const CommonIssues = lazy(() => import("./docs/troubleshooting/common-issues.mdx"));
const Performance = lazy(() => import("./docs/troubleshooting/performance.mdx"));
const FAQ = lazy(() => import("./docs/troubleshooting/faq.mdx"));

// Reference
const KeyboardShortcuts = lazy(() => import("./docs/reference/keyboard-shortcuts.mdx"));
const SlashCommands = lazy(() => import("./docs/reference/slash-commands.mdx"));
const ToolCatalog = lazy(() => import("./docs/reference/tool-catalog.mdx"));
const Changelog = lazy(() => import("./docs/reference/changelog.mdx"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  useEffect(() => {
    const helloWorldApi = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log(response.data.message);
      } catch (e) {
        console.error(e, `errored out requesting / api`);
      }
    };
    helloWorldApi();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ConditionalHeader />
        <Analytics />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog-docs" element={<BlogDocs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/download" element={<Download />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-email-required" element={<VerifyEmailRequired />} />

          {/* Purchase Routes */}
          <Route path="/purchase-success" element={<PurchaseSuccess />} />

          {/* Customer Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/licenses/:licenseId" element={
            <ProtectedRoute>
              <LicenseDetails />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/purchases" element={
            <ProtectedRoute>
              <PurchaseHistory />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/licenses" element={
            <ProtectedRoute>
              <AdminLicenses />
            </ProtectedRoute>
          } />
          <Route path="/admin/licenses/create" element={
            <ProtectedRoute>
              <AdminCreateLicense />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/emails" element={
            <ProtectedRoute>
              <AdminEmails />
            </ProtectedRoute>
          } />
          <Route path="/admin/email-rules" element={
            <ProtectedRoute>
              <AdminEmailRules />
            </ProtectedRoute>
          } />

          {/* Documentation Routes */}
          <Route path="/docs" element={<Docs />}>
            <Route index element={<DocsHome />} />
            <Route path="getting-started">
              <Route path="introduction" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Introduction />
                </Suspense>
              } />
              <Route path="installation" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Installation />
                </Suspense>
              } />
              <Route path="quick-start" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <QuickStart />
                </Suspense>
              } />
              <Route path="system-requirements" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <SystemRequirements />
                </Suspense>
              } />
              <Route path="licensing" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Licensing />
                </Suspense>
              } />
            </Route>
            <Route path="core-features">
              <Route path="chat-interface" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <ChatInterface />
                </Suspense>
              } />
              <Route path="ai-providers" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <AIProviders />
                </Suspense>
              } />
              <Route path="mcp-tools" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <MCPTools />
                </Suspense>
              } />
              <Route path="browser-automation" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <BrowserAutomation />
                </Suspense>
              } />
              <Route path="notes" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Notes />
                </Suspense>
              } />
            </Route>
            <Route path="advanced-features">
              <Route path="pr-review" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <PRReview />
                </Suspense>
              } />
              <Route path="automated-testing" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <AutomatedTesting />
                </Suspense>
              } />
              <Route path="bug-reproduction" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <BugReproduction />
                </Suspense>
              } />
              <Route path="scheduled-tasks" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <ScheduledTasks />
                </Suspense>
              } />
              <Route path="dictation" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Dictation />
                </Suspense>
              } />
              <Route path="hooks-system" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <HooksSystem />
                </Suspense>
              } />
              <Route path="windows-automation" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <WindowsAutomation />
                </Suspense>
              } />
              <Route path="exploratory-testing" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <ExploratoryTesting />
                </Suspense>
              } />
              <Route path="dependency-map" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <DependencyMap />
                </Suspense>
              } />
              <Route path="recall" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Recall />
                </Suspense>
              } />
              <Route path="rag-library" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <RAGLibrary />
                </Suspense>
              } />
              <Route path="code-agent" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <CodeAgent />
                </Suspense>
              } />
              <Route path="dnd-assistant" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <DNDAssistant />
                </Suspense>
              } />
            </Route>

            <Route path="configuration">
              <Route path="settings-overview" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <SettingsOverview />
                </Suspense>
              } />
              <Route path="ai-configuration" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <AIConfiguration />
                </Suspense>
              } />
              <Route path="mcp-servers" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <MCPServers />
                </Suspense>
              } />
              <Route path="browser-profiles" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <BrowserProfiles />
                </Suspense>
              } />
              <Route path="themes" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Themes />
                </Suspense>
              } />
            </Route>

            <Route path="guides">
              <Route path="azure-devops-setup" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <AzureDevOpsSetup />
                </Suspense>
              } />
              <Route path="sql-database-setup" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <SQLDatabaseSetup />
                </Suspense>
              } />
              <Route path="outlook-integration" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <OutlookIntegration />
                </Suspense>
              } />
              <Route path="workflow-examples" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <WorkflowExamples />
                </Suspense>
              } />
              <Route path="best-practices" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <BestPractices />
                </Suspense>
              } />
              <Route path="dynamics-365-power-platform" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Dynamics365PowerPlatform />
                </Suspense>
              } />
            </Route>

            <Route path="troubleshooting">
              <Route path="common-issues" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <CommonIssues />
                </Suspense>
              } />
              <Route path="performance" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Performance />
                </Suspense>
              } />
              <Route path="faq" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <FAQ />
                </Suspense>
              } />
            </Route>

            <Route path="reference">
              <Route path="keyboard-shortcuts" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <KeyboardShortcuts />
                </Suspense>
              } />
              <Route path="slash-commands" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <SlashCommands />
                </Suspense>
              } />
              <Route path="tool-catalog" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <ToolCatalog />
                </Suspense>
              } />
              <Route path="changelog" element={
                <Suspense fallback={<div className="p-8">Loading...</div>}>
                  <Changelog />
                </Suspense>
              } />
            </Route>
          </Route>
        </Routes>
        <ConditionalFooter />
      </BrowserRouter>
    </div>
  );
}

export default App;